import { z } from "zod";

/// Wire-shape mirrors of the shore M16 configuration DTOs (calendars, shifts,
/// assignments, settings). Split from attendance.schema.ts so each file stays
/// readable; same contract, same .strict() discipline.

export const holidaySchema = z
  .object({
    id: z.string(),
    holidayDate: z.string(),
    description: z.string(),
    /// Generated from the weekly pattern rather than entered by hand.
    /// Regeneration replaces only these, so named holidays survive.
    weeklyOff: z.boolean(),
    isOptional: z.boolean().optional(),
    isWorkingOverride: z.boolean().optional(),
    isHalfDay: z.boolean(),
    halfDayPeriod: z.enum(["first_half", "second_half"]).nullable().optional(),
  })
  .strict();
export type Holiday = z.infer<typeof holidaySchema>;

export const holidayListSchema = z.object({ items: z.array(holidaySchema) }).strict();

export const calendarSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    fromDate: z.string(),
    toDate: z.string(),
    weeklyOffDays: z.array(z.number()),
    weeklyOffDayOfWeek: z.number().nullable(),
    isDefault: z.boolean(),
    /// Where the holidays were last imported from, so the import dialog can
    /// offer the same choice again. Null on a hand-built calendar.
    country: z.string().nullable(),
    subdivision: z.string().nullable(),
    holidayCount: z.number(),
    /// Negative once coverage has already run out. The sweep refuses to judge
    /// days past this, so it is a real operational number, not decoration.
    coverageEndsInDays: z.number(),
    version: z.number(),
    createdAt: z.string(),
  })
  .strict();
export type Calendar = z.infer<typeof calendarSchema>;

export const calendarDetailSchema = calendarSchema
  .extend({ holidays: z.array(holidaySchema) })
  .strict();
export type CalendarDetail = z.infer<typeof calendarDetailSchema>;

export const calendarListSchema = z.object({ items: z.array(calendarSchema) }).strict();

export const SHIFT_CHECK_IN_OUT_MODES = ["alternating", "strict_log_type"] as const;
export const shiftCheckInOutModeSchema = z.enum(SHIFT_CHECK_IN_OUT_MODES);

export const SHIFT_WORKING_HOURS_MODES = ["first_in_last_out", "every_valid_pair"] as const;
export const shiftWorkingHoursModeSchema = z.enum(SHIFT_WORKING_HOURS_MODES);

export const shiftSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    startTime: z.string(),
    endTime: z.string(),
    /// True when the shift crosses midnight, which changes how it is dated:
    /// the attendance belongs to the day the shift STARTED.
    isOvernight: z.boolean(),
    holidayCalendarId: z.string().nullable(),
    determineCheckInOut: shiftCheckInOutModeSchema,
    workingHoursMode: shiftWorkingHoursModeSchema,
    halfDayThresholdHours: z.number().nullable(),
    absentThresholdHours: z.number().nullable(),
    beginCheckInBeforeMinutes: z.number(),
    allowCheckOutAfterMinutes: z.number(),
    lateEntryGraceMinutes: z.number(),
    earlyExitGraceMinutes: z.number(),
    enableAutoAttendance: z.boolean(),
    markAutoAttendanceOnHolidays: z.boolean(),
    enableLateEntryMarking: z.boolean(),
    enableEarlyExitMarking: z.boolean(),
    processAttendanceAfter: z.string().nullable(),
    lastSyncOfCheckin: z.string().nullable(),
    color: z.string().nullable(),
    timezone: z.string().nullable(),
    assignedCount: z.number(),
    version: z.number(),
  })
  .strict();
export type Shift = z.infer<typeof shiftSchema>;

export const shiftListSchema = z.object({ items: z.array(shiftSchema) }).strict();

export const SHIFT_ASSIGNMENT_STATUSES = ["active", "inactive"] as const;

export const assignmentSchema = z
  .object({
    id: z.string(),
    membershipId: z.string(),
    shiftId: z.string(),
    startDate: z.string(),
    endDate: z.string().nullable(),
    status: z.enum(SHIFT_ASSIGNMENT_STATUSES),
    version: z.number(),
  })
  .strict();
export type Assignment = z.infer<typeof assignmentSchema>;

export const assignmentListSchema = z.object({ items: z.array(assignmentSchema) }).strict();

export const assignmentConflictSchema = z
  .object({
    membershipId: z.string(),
    /// The shift already covering those dates, so the screen can name it.
    shiftId: z.string(),
    startDate: z.string(),
  })
  .strict();
export type AssignmentConflict = z.infer<typeof assignmentConflictSchema>;

export const bulkAssignResultSchema = z
  .object({
    created: z.number(),
    /// Moved off another shift. Only non-zero when the request asked to move.
    moved: z.number(),
    /// Already on this shift over those dates, so nothing was needed.
    alreadyOnShift: z.number(),
    /// People skipped because they already had an overlapping assignment.
    /// Named rather than silently dropped, so an admin can see who and why.
    skippedMembershipIds: z.array(z.string()),
    conflicts: z.array(assignmentConflictSchema),
  })
  .strict();
export type BulkAssignResult = z.infer<typeof bulkAssignResultSchema>;

export const attendanceSettingsSchema = z
  .object({
    /// Days an unresolved day survives before it becomes an absence.
    graceWindowDays: z.number(),
    allowSelfCheckin: z.boolean(),
    version: z.number(),
  })
  .strict();
export type AttendanceSettings = z.infer<typeof attendanceSettingsSchema>;

export const weekOffResultSchema = z
  .object({ generated: z.number(), removed: z.number() })
  .strict();

export const cancelResultSchema = z
  .object({ cancelled: z.literal(true), releasedCheckins: z.number() })
  .strict();
export type CancelResult = z.infer<typeof cancelResultSchema>;

export const processResultSchema = z
  .object({ groupsSeen: z.number(), attendanceCreated: z.number(), skipped: z.number() })
  .strict();

export const deletedSchema = z.object({ deleted: z.literal(true) }).strict();

/// Public holiday reference data, generated offline from the same source
/// Frappe HR uses and served by the shore. See the shore's `holiday-catalog`.
export const catalogCountrySchema = z
  .object({ code: z.string(), name: z.string(), hasSubdivisions: z.boolean() })
  .strict();
export type CatalogCountry = z.infer<typeof catalogCountrySchema>;

export const catalogCountryListSchema = z
  .object({
    items: z.array(catalogCountrySchema),
    /// The years the catalogue can answer for, so a calendar running past them
    /// gets told rather than shown an inexplicably short preview.
    bounds: z.object({ yearFrom: z.number(), yearTo: z.number() }).strict(),
  })
  .strict();

export const catalogSubdivisionSchema = z.object({ code: z.string(), name: z.string() }).strict();
export type CatalogSubdivision = z.infer<typeof catalogSubdivisionSchema>;

export const catalogSubdivisionListSchema = z
  .object({ items: z.array(catalogSubdivisionSchema) })
  .strict();

/// What the import would do to one date. `weekOff` means the day is already a
/// non-working day on this calendar, so the holiday cannot be added on top of
/// it, which is worth saying rather than silently dropping.
export const importOutcomeSchema = z.enum(["new", "existing", "weekOff"]);
export type ImportOutcome = z.infer<typeof importOutcomeSchema>;

export const previewedHolidaySchema = z
  .object({
    date: z.string(),
    name: z.string(),
    optional: z.boolean(),
    outcome: importOutcomeSchema,
  })
  .strict();
export type PreviewedHoliday = z.infer<typeof previewedHolidaySchema>;

export const importPreviewSchema = z
  .object({
    from: z.string(),
    to: z.string(),
    yearFrom: z.number(),
    yearTo: z.number(),
    holidays: z.array(previewedHolidaySchema),
  })
  .strict();
export type ImportPreview = z.infer<typeof importPreviewSchema>;

export const importResultSchema = z
  .object({
    added: z.number(),
    skipped: z.number(),
    country: z.string(),
    subdivision: z.string().nullable(),
  })
  .strict();
export type ImportResult = z.infer<typeof importResultSchema>;
